import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { ButtonSecondary } from "@nova/component-btn"
import { Information, Radar, Rapid, Urban, Forest } from "@nova/component-icons"

import type { JSX } from "react"

export type ProcessingMode = "general" | "urban" | "rapid" | "general-hotspot"

export interface ProcessingOptionsModalProps {
    closeModal: () => void
    enabledModes?: ProcessingMode[]
}

export const ProcessingOptionsModal = (props: ProcessingOptionsModalProps) => {
    const { closeModal, enabledModes = ["general", "urban", "rapid"] } = props
    return (
        <HighPriorityNovaModalContainer
            icon={Information}
            title="How Processing Options Work"
            hideTitleUnderline
            bottomBar={
                <div className="flex w-full justify-end">
                    <ButtonSecondary onClick={closeModal} title="Okay" />
                </div>
            }
            closeModal={closeModal}
            className="opacity-90"
        >
            <div className="flex gap-[11px] pt-2">
                {enabledModes.includes("general") && (
                    <ProcessingModeTile
                        icon={<Forest />}
                        title="General Mode"
                        description="Fast, reliable orthomosaics built for speed and accuracy across most terrain."
                    />
                )}
                {enabledModes.includes("general-hotspot") && (
                    <ProcessingModeTile
                        icon={<Radar />}
                        title="General Mode"
                        description="Our reliable hotspot detection built for accuracy."
                    />
                )}
                {enabledModes.includes("urban") && (
                    <ProcessingModeTile
                        icon={<Urban />}
                        title="Urban Mode"
                        description="Slower processing, optimized for straighter edges and detailed urban environments."
                    />
                )}
                {enabledModes.includes("rapid") && (
                    <ProcessingModeTile
                        icon={<Rapid />}
                        title="Rapid Mode"
                        description="A faster orthomosaic using our realtime processing engine."
                    />
                )}
            </div>
        </HighPriorityNovaModalContainer>
    )
}

interface ProcessingModeTileProps {
    icon: JSX.Element
    title: string
    tag?: JSX.Element
    description: string
}

const ProcessingModeTile = (props: ProcessingModeTileProps) => (
    <div className="flex flex-col w-[200px] h-[185px] p-[16px] items-start gap-[32px] rounded-xl border border-[rgba(255,255,255,0.05)]">
        <div className="w-[40px] h-[40px]">
            <IconWrapper>{props.icon}</IconWrapper>
        </div>
        <div className="flex flex-col gap-[8px] text-gray-0">
            <div className="flex gap-2">
                <h3 className="text-[16px] leading-[19.2px] font-normal text-white whitespace-nowrap">
                    {props.title}
                </h3>
                {props.tag}
            </div>
            <p className="self-stretch text-[12px] leading-[18px] font-normal text-[#B1B1B3]">
                {props.description}
            </p>
        </div>
    </div>
)

const IconWrapper = ({ children }: React.PropsWithChildren) => (
    <div className="relative flex items-center justify-center">
        {/* four corners of a partial border */}
        <div
            className="absolute top-0 left-0 w-[10px] h-[10px]
              border-t border-l
              border-white/10
              rounded-tl-[6px]
              pointer-events-none"
        ></div>

        <div
            className="absolute top-0 right-0 w-[10px] h-[10px]
              border-t border-r
              border-white/10
              rounded-tr-[6px]
              pointer-events-none"
        ></div>

        <div
            className="absolute bottom-0 left-0 w-[10px] h-[10px]
              border-b border-l
              border-white/10
              rounded-bl-[6px]
              pointer-events-none"
        ></div>

        <div
            className="absolute bottom-0 right-0 w-[10px] h-[10px]
              border-b border-r
              border-white/10
              rounded-br-[6px]
              pointer-events-none"
        ></div>

        {/* icon centred inside wrapper */}
        <div className="rounded m-[3px] w-[34px] h-[34px] bg-[rgba(12,12,15,0.20)] shadow-[0_2px_8px_0_rgba(255,255,255,0.10)] relative flex items-center justify-center">
            {children}
        </div>
    </div>
)
